import { useState, useEffect, useCallback } from 'react'
import { filterMyMessages, buildContextualChunks } from '../utils/filters'
import {
  listParticipants, saveParticipant, deleteParticipant, mergeParticipants,
  analyzeParticipant, getParticipantProfile, deepAnalysis, clearChunks, embedChunks,
  getChunksStats, extractFacts, getPublicParticipant, updateParticipantVisibility,
  createParticipantManual, createInviteForParticipant,
  createParticipantFromPlaylistStart, pollThreadsJob,
} from '../utils/api'
import { getModelPref } from '../utils/modelPrefs'
import FactsViewer from './FactsViewer'
import VoiceNotesViewer from './VoiceNotesViewer'
import { VisibilitySettings } from './PublicParticipantPage'
import { sanitizeProfile } from '../utils/sanitizeProfile'

const C = {
  bg:         '#05050d',
  surface:    'rgba(255,255,255,0.028)',
  surface2:   'rgba(255,255,255,0.05)',
  border:     'rgba(180,185,230,0.09)',
  borderHi:   'rgba(180,185,230,0.22)',
  accent:     '#b8bdd4',
  accentDim:  'rgba(180,185,212,0.12)',
  text:       '#dce0f0',
  textMuted:  '#8890aa',
  green:      '#4dc88a',
  greenDim:   'rgba(77,200,138,0.12)',
  red:        '#d45a5a',
  redDim:     'rgba(212,90,90,0.12)',
  gold:       '#c8a96e',
}
const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

function GlassPanel({ children, style = {} }) {
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      padding: '20px 24px',
      backdropFilter: 'blur(8px)',
      ...style,
    }}>
      {children}
    </div>
  )
}

function Tag({ children, color = C.textMuted }) {
  return (
    <span style={{
      fontFamily: font.mono, fontSize: 10, letterSpacing: '0.06em',
      textTransform: 'uppercase', color,
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${C.border}`,
      borderRadius: 4, padding: '2px 7px',
    }}>
      {children}
    </span>
  )
}

// Строим контекст для участника из сообщений Telegram
function buildParticipantData(messages, participantAliases) {
  const aliasSet = new Set(participantAliases.map(a => a.toLowerCase().trim()))
  const participantMessages = messages.filter(m =>
    m.from && aliasSet.has(m.from.toLowerCase().trim())
  )
  if (!participantMessages.length) return null

  const withContext = participantMessages.map(m => ({
    myText: m.text,
    contextText: m.reply_to_text || null,
    date: m.date,
    chatName: m.chatName,
    chatType: m.chatType,
  }))

  const allChunks = buildContextualChunks(withContext, 30)
  const picks = allChunks.length <= 5 ? allChunks : [
    allChunks[0],
    allChunks[Math.floor(allChunks.length * 0.25)],
    allChunks[Math.floor(allChunks.length * 0.5)],
    allChunks[Math.floor(allChunks.length * 0.75)],
    allChunks[allChunks.length - 1],
  ]

  const chatTypeLabel = participantMessages[0]?.chatType === 'group'
    ? 'Групповые чаты (мат = манера общения)'
    : 'Личная переписка'

  const context = `## Сообщения участника (${chatTypeLabel})\n` +
    picks.map(c => c.text).join('\n\n---\n\n')

  // Возвращаем полные объекты чанков — бэкенд embed-chunks ждёт {text, source, chatType, dateRange, ...}
  return { context, allChunkTexts: allChunks }
}

// Backward compat wrapper
function buildParticipantContext(messages, participantAliases, myName) {
  const data = buildParticipantData(messages, participantAliases)
  return data ? data.context : null
}

// ─── Карточка участника ────────────────────────────────────────────────────

function ParticipantCard({ participant, messages, onUpdated, isOwner, allParticipants = [] }) {
  const [expanded, setExpanded] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [deepAnalyzing, setDeepAnalyzing] = useState(false)
  const [factsLoading, setFactsLoading] = useState(false)
  const [factsCount, setFactsCount] = useState(null)
  const [factsSubTab, setFactsSubTab] = useState('facts')
  const [showFacts, setShowFacts] = useState(false)
  const [profile, setProfile] = useState(null)
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [copyMsg, setCopyMsg] = useState('')
  const [clearConfirm, setClearConfirm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [dataStats, setDataStats] = useState(null)
  const [showMergePicker, setShowMergePicker] = useState(false)
  const [merging, setMerging] = useState(false)
  const [publicSettings, setPublicSettings] = useState(null)
  const [visibilitySaving, setVisibilitySaving] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)
  const [showVisibility, setShowVisibility] = useState(false)
  const [inviteUrl, setInviteUrl]   = useState(null)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteCopied, setInviteCopied]   = useState(false)

  const publicUrl = `${window.location.origin}/u/${participant.id}`

  const loadProfile = useCallback(async () => {
    if (profile || !participant.has_profile) return
    setLoadingProfile(true)
    try {
      const data = await getParticipantProfile(participant.id)
      setProfile(sanitizeProfile(data.profile))
    } finally {
      setLoadingProfile(false)
    }
  }, [participant.id, participant.has_profile, profile])

  useEffect(() => {
    if (expanded) {
      loadProfile()
      getChunksStats(`participant_${participant.id}`).then(s => { if (!s.error) setDataStats(s) }).catch(() => {})
      if (!publicSettings) getPublicParticipant(participant.id).then(d => setPublicSettings(d.public_settings)).catch(() => {})
    }
  }, [expanded, loadProfile, participant.id])

  const handleAnalyze = async () => {
    const aliases = participant.aliases || [participant.display_name]
    const participantUserId = `participant_${participant.id}`
    setAnalyzing(true)
    try {
      // Шаг 1: embed текущие данные в DB (если есть загруженные сообщения)
      if (messages?.length) {
        const data = buildParticipantData(messages, aliases)
        if (data?.allChunkTexts?.length) {
          await embedChunks(participantUserId, data.allChunkTexts)
        }
      }
      // Шаг 2: анализ — бэкенд сам берёт репрезентативную выборку из всех накопленных чанков
      const result = await analyzeParticipant(participant.id, '')
      if (result.error) throw new Error(result.error)
      setProfile(null)
      onUpdated()
      getChunksStats(participantUserId).then(s => { if (!s.error) setDataStats(s) }).catch(() => {})
    } catch (e) {
      const isNoData = e.message.includes('Нет данных')
      const isSelf = aliases.includes('__self__')
      if (isNoData && isSelf) {
        alert(`Этот участник сохранён с устаревшим ником "__self__".\n\nУдали его и добавь заново из списка Telegram — теперь собеседник отображается под правильным именем (названием чата).`)
      } else if (isNoData) {
        alert(`Нет данных для анализа.\n\nЗагрузи Telegram-файлы в разделе "Обновить данные" и нажми "Начать анализ" — после этого данные участника попадут в базу.`)
      } else {
        alert(`Ошибка анализа: ${e.message}`)
      }
    } finally {
      setAnalyzing(false)
    }
  }

  const handleDeepAnalyze = async () => {
    setDeepAnalyzing(true)
    try {
      const userId = `participant_${participant.id}`
      const result = await deepAnalysis(userId)
      if (result.error) throw new Error(result.error)
      setProfile(null)
      onUpdated()
      alert(`Глубокий анализ завершён: ${result.chunks_analyzed} чанков, ${result.batches} батчей · $${result.usage?.cost_usd?.toFixed(4) || '?'}`)
    } catch (e) {
      alert(`Ошибка глубокого анализа: ${e.message}`)
    } finally {
      setDeepAnalyzing(false)
    }
  }

  const handleDelete = async () => {
    await deleteParticipant(participant.id)
    onUpdated()
  }

  const handleMerge = async (sourceId) => {
    setMerging(true)
    setShowMergePicker(false)
    try {
      const r = await mergeParticipants(participant.id, sourceId)
      if (r.error) throw new Error(r.error)
      setProfile(null)
      onUpdated()
    } catch (e) {
      alert(`Ошибка объединения: ${e.message}`)
    } finally {
      setMerging(false)
    }
  }

  const copyPassword = () => {
    navigator.clipboard.writeText(participant.password)
    setCopyMsg('скопировано')
    setTimeout(() => setCopyMsg(''), 1500)
  }

  const p = profile?.personality_data
  const analyzed = !!participant.analyzed_at

  return (
    <GlassPanel style={{ marginBottom: 12 }}>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}
      >
        {/* Аватар */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: analyzed ? C.accentDim : 'rgba(255,255,255,0.04)',
          border: `1px solid ${analyzed ? C.accent : C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: font.heading, fontSize: 18, color: analyzed ? C.accent : C.textMuted,
        }}>
          {participant.display_name[0]?.toUpperCase()}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: font.heading, fontSize: 17, color: C.text }}>
            {participant.display_name}
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, fontFamily: font.body, marginTop: 2 }}>
            {participant.message_count} сообщ.
            {participant.aliases?.length > 1 &&
              <span style={{ marginLeft: 6, color: C.textMuted }}>· {participant.aliases.join(', ')}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {analyzed
            ? <Tag color={C.green}>профиль готов</Tag>
            : <Tag color={C.textMuted}>не анализирован</Tag>}
          <span style={{ color: C.textMuted, fontSize: 12 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>

          {/* Пароль — только для владельца */}
          {isOwner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
              padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 7, border: `1px solid ${C.border}` }}>
              <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted }}>ПАРОЛЬ:</span>
              <span style={{ fontFamily: font.mono, fontSize: 13, color: C.gold, flex: 1 }}>{participant.password}</span>
              <button onClick={copyPassword}
                style={{ fontSize: 11, fontFamily: font.mono, color: copyMsg ? C.green : C.textMuted,
                  background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>
                {copyMsg || 'копировать'}
              </button>
            </div>
          )}

          {/* Статистика данных */}
          {dataStats && dataStats.total > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginBottom: 12,
              padding: '6px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: 6,
              border: `1px solid ${C.border}`, fontFamily: font.mono, fontSize: 11 }}>
              <span style={{ color: C.textMuted }}>данных:</span>
              {(dataStats.by_type?.group || 0) > 0 && <span style={{ color: C.textMuted }}>группы ~{((dataStats.by_type.group) * 40).toLocaleString('ru')} сообщ.</span>}
              {(dataStats.by_type?.personal || 0) > 0 && <span style={{ color: C.textMuted }}>личные ~{((dataStats.by_type.personal) * 40).toLocaleString('ru')} сообщ.</span>}
              {(dataStats.by_type?.journal || 0) > 0 && <span style={{ color: C.textMuted }}>дневник {dataStats.by_type.journal} зап.</span>}
              {(dataStats.by_type?.threads || 0) > 0 && <span style={{ color: C.textMuted }}>threads {dataStats.by_type.threads}</span>}
              {(dataStats.by_type?.resume || 0) > 0 && <span style={{ color: C.textMuted }}>резюме ✓</span>}
            </div>
          )}

          {/* Кнопки действий */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button onClick={handleAnalyze} disabled={analyzing || deepAnalyzing}
              style={{ padding: '7px 16px', background: analyzing ? 'transparent' : C.accent,
                color: analyzing ? C.textMuted : C.bg,
                border: `1px solid ${analyzing ? C.border : C.accent}`,
                borderRadius: 7, fontFamily: font.mono, fontSize: 12, cursor: analyzing ? 'default' : 'pointer' }}>
              {analyzing ? 'анализируем...' : analyzed ? 'переанализировать' : 'запустить анализ'}
            </button>
            {isOwner && (
              <button onClick={handleDeepAnalyze} disabled={deepAnalyzing || analyzing}
                title="Полный map-reduce анализ всех накопленных данных участника"
                style={{ padding: '7px 14px', background: 'transparent',
                  color: deepAnalyzing ? C.gold : C.textMuted,
                  border: `1px solid ${deepAnalyzing ? C.gold + '60' : C.border}`,
                  borderRadius: 7, fontFamily: font.mono, fontSize: 12, cursor: deepAnalyzing ? 'wait' : 'pointer' }}>
                {deepAnalyzing ? '⏳ глубокий...' : '◎ глубокий'}
              </button>
            )}
            {isOwner && (
              <button
                onClick={async () => {
                  if (showFacts) { setShowFacts(false); return }
                  if (factsCount !== null) { setShowFacts(true); return }
                  setFactsLoading(true)
                  const uid = `participant_${participant.id}`
                  const r = await extractFacts(uid)
                  setFactsLoading(false)
                  if (!r.error) { setFactsCount(r.extracted); setShowFacts(true) }
                }}
                disabled={factsLoading}
                title="Извлечь факты из накопленных данных участника"
                style={{ padding: '7px 14px', background: 'transparent',
                  color: factsLoading ? C.accent : C.textMuted,
                  border: `1px solid ${factsLoading ? C.accent + '60' : C.border}`,
                  borderRadius: 7, fontFamily: font.mono, fontSize: 12, cursor: factsLoading ? 'wait' : 'pointer' }}>
                {factsLoading ? '⏳ факты...' : factsCount !== null ? `◈ факты · ${factsCount}` : '◈ факты'}
              </button>
            )}
            {isOwner && (
              clearConfirm ? (
                <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: 'rgba(220,90,90,0.8)', fontFamily: font.mono }}>Удалить данные?</span>
                  <button onClick={async () => {
                    setClearConfirm(false)
                    const r = await clearChunks(`participant_${participant.id}`)
                    if (!r.error) onUpdated()
                  }} style={{ padding: '5px 10px', background: 'rgba(220,90,90,0.15)', color: 'rgba(220,90,90,0.9)', border: '1px solid rgba(220,90,90,0.5)', borderRadius: 6, fontFamily: font.mono, fontSize: 11, cursor: 'pointer' }}>да</button>
                  <button onClick={() => setClearConfirm(false)} style={{ padding: '5px 10px', background: 'transparent', color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: font.mono, fontSize: 11, cursor: 'pointer' }}>нет</button>
                </span>
              ) : (
                <button onClick={() => setClearConfirm(true)}
                  style={{ padding: '7px 12px', background: 'transparent', color: 'rgba(220,90,90,0.6)',
                    border: `1px solid ${C.border}`, borderRadius: 7, fontFamily: font.mono, fontSize: 11, cursor: 'pointer' }}>
                  ✕ очистить данные
                </button>
              )
            )}
            {deleteConfirm ? (
              <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: C.red, fontFamily: font.mono }}>Удалить участника?</span>
                <button onClick={() => { setDeleteConfirm(false); handleDelete() }} style={{ padding: '5px 10px', background: 'rgba(200,60,60,0.15)', color: C.red, border: `1px solid ${C.red}80`, borderRadius: 6, fontFamily: font.mono, fontSize: 11, cursor: 'pointer' }}>да</button>
                <button onClick={() => setDeleteConfirm(false)} style={{ padding: '5px 10px', background: 'transparent', color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: font.mono, fontSize: 11, cursor: 'pointer' }}>нет</button>
              </span>
            ) : (
              <button onClick={() => setDeleteConfirm(true)}
                style={{ padding: '7px 14px', background: 'transparent', color: C.red,
                  border: `1px solid ${C.border}`, borderRadius: 7, fontFamily: font.mono, fontSize: 12, cursor: 'pointer' }}>
                удалить
              </button>
            )}
            {/* Кнопка объединения */}
            {isOwner && allParticipants.filter(p => p.id !== participant.id).length > 0 && (
              <button onClick={() => setShowMergePicker(v => !v)} disabled={merging}
                style={{ padding: '7px 14px', background: 'transparent',
                  color: merging ? C.textMuted : showMergePicker ? C.accent : C.textMuted,
                  border: `1px solid ${showMergePicker ? C.accent + '60' : C.border}`,
                  borderRadius: 7, fontFamily: font.mono, fontSize: 11, cursor: merging ? 'wait' : 'pointer' }}>
                {merging ? '...' : '↔ объединить'}
              </button>
            )}
          </div>

          {/* Пикер объединения */}
          {showMergePicker && (
            <div style={{ marginBottom: 14, padding: '10px 12px',
              background: 'rgba(255,255,255,0.025)', borderRadius: 8, border: `1px solid ${C.border}` }}>
              <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Объединить с — данные и алиасы будут добавлены к этому профилю, второй удалён:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {allParticipants.filter(p => p.id !== participant.id).map(p => (
                  <button key={p.id} onClick={() => handleMerge(p.id)}
                    style={{ textAlign: 'left', padding: '6px 10px',
                      background: C.surface2, border: `1px solid ${C.border}`,
                      borderRadius: 6, cursor: 'pointer',
                      fontFamily: font.body, fontSize: 13, color: C.text }}>
                    {p.display_name}
                    {p.aliases?.length > 1 && (
                      <span style={{ marginLeft: 6, fontFamily: font.mono, fontSize: 10, color: C.textMuted }}>
                        · {p.aliases.join(', ')}
                      </span>
                    )}
                    <span style={{ marginLeft: 8, fontFamily: font.mono, fontSize: 10, color: C.textMuted }}>
                      {p.message_count} сообщ.
                    </span>
                    {p.has_profile && (
                      <span style={{ marginLeft: 6, fontFamily: font.mono, fontSize: 10, color: C.green }}>профиль ✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Профиль */}
          {loadingProfile && <div style={{ color: C.textMuted, fontSize: 13 }}>Загрузка профиля...</div>}
          {profile && p && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {p.summary && (
                <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6, fontFamily: font.body,
                  padding: '10px 14px', background: C.surface2, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  {p.summary}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {p.mbti && <Tag color={C.accent}>{p.mbti}</Tag>}
                {p.archetype && <Tag color={C.gold}>{p.archetype}</Tag>}
              </div>
              {p.strengths?.length > 0 && (
                <div>
                  <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Сильные стороны</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {p.strengths.slice(0, 6).map((s, i) => (
                      <span key={i} style={{ fontSize: 12, color: C.text, padding: '2px 8px',
                        background: C.surface2, borderRadius: 4, border: `1px solid ${C.border}` }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {analyzed && !loadingProfile && !profile && (
            <div style={{ fontSize: 12, color: C.textMuted }}>Нажми чтобы загрузить профиль</div>
          )}

          {isOwner && showFacts && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 17, fontWeight: 300, color: '#e8e8f0' }}>◈ Долгосрочная память</span>
                <button onClick={() => setShowFacts(false)} style={{ fontSize: 11, background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer' }}>скрыть</button>
              </div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: `1px solid ${C.border}` }}>
                {[{ id: 'facts', label: 'Факты' }, { id: 'voice', label: 'Голос и манера' }].map(t => (
                  <button
                    key={t.id}
                    onClick={() => setFactsSubTab(t.id)}
                    style={{
                      padding: '6px 12px', background: 'none', border: 'none',
                      borderBottom: `2px solid ${factsSubTab === t.id ? C.accent : 'transparent'}`,
                      color: factsSubTab === t.id ? C.text : C.textMuted,
                      fontFamily: font.mono, fontSize: 11, cursor: 'pointer', marginBottom: -1,
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {factsSubTab === 'facts' ? (
                <FactsViewer userId={`participant_${participant.id}`} onCountChange={setFactsCount} />
              ) : (
                <VoiceNotesViewer userId={`participant_${participant.id}`} />
              )}
            </div>
          )}

          {/* ── Ссылка для входа (инвайт привязанный к этому участнику) ── */}
          {isOwner && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: inviteUrl ? 10 : 0 }}>
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 15, fontWeight: 400, color: '#e8e8f0', flex: 1 }}>
                  Ссылка для входа
                </span>
                <button
                  onClick={async () => {
                    setInviteLoading(true); setInviteUrl(null)
                    try {
                      const data = await createInviteForParticipant(participant.id, participant.display_name)
                      if (data.invite_url) setInviteUrl(data.invite_url)
                    } catch (e) { alert(e.message) }
                    finally { setInviteLoading(false) }
                  }}
                  disabled={inviteLoading}
                  style={{ padding: '4px 12px', borderRadius: 5, border: `1px solid ${C.borderHi}`, background: 'transparent', color: inviteLoading ? C.textMuted : C.accent, fontFamily: 'Space Mono, monospace', fontSize: 10, cursor: inviteLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  {inviteLoading ? '⏳' : '+ создать'}
                </button>
              </div>
              {inviteUrl && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <code style={{ flex: 1, fontFamily: 'Space Mono, monospace', fontSize: 10, color: C.accent, background: 'rgba(255,255,255,0.04)', padding: '5px 8px', borderRadius: 5, border: `1px solid ${C.border}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inviteUrl}
                  </code>
                  <button
                    onClick={() => { navigator.clipboard?.writeText(inviteUrl).catch(() => {}); setInviteCopied(true); setTimeout(() => setInviteCopied(false), 2000) }}
                    style={{ padding: '5px 10px', borderRadius: 5, border: `1px solid ${C.borderHi}`, background: 'transparent', color: inviteCopied ? C.green : C.accent, fontFamily: 'Space Mono, monospace', fontSize: 10, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {inviteCopied ? '✓' : '⎘ копировать'}
                  </button>
                </div>
              )}
              <p style={{ margin: '6px 0 0', fontSize: 11, color: C.textMuted, fontFamily: font.body }}>
                Человек открывает ссылку и входит через Google или пароль — аккаунт автоматически привязывается к этому участнику.
              </p>
            </div>
          )}

          {/* ── Публичная страница ── (не показываем для is_owner участников) */}
          {isOwner && !participant.is_owner && (
            <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 15, fontWeight: 400, color: '#e8e8f0' }}>
                  ◈ Публичная страница
                </span>
                <button
                  onClick={() => setShowVisibility(v => !v)}
                  style={{ fontSize: 10, background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontFamily: 'Space Mono, monospace' }}
                >
                  {showVisibility ? 'скрыть' : 'видимость'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: showVisibility ? 12 : 0 }}>
                <code style={{ flex: 1, fontFamily: 'Space Mono, monospace', fontSize: 10, color: C.silverLo, background: 'rgba(255,255,255,0.04)', padding: '5px 8px', borderRadius: 5, border: `1px solid ${C.border}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {publicUrl}
                </code>
                <button
                  onClick={() => { navigator.clipboard?.writeText(publicUrl).catch(() => {}); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000) }}
                  style={{ padding: '5px 12px', borderRadius: 5, border: `1px solid ${C.borderHi}`, background: 'transparent', color: linkCopied ? '#6dc88a' : C.accent, fontFamily: 'Space Mono, monospace', fontSize: 10, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {linkCopied ? '✓' : '⎘ копировать'}
                </button>
                <a href={publicUrl} target="_blank" rel="noreferrer"
                  style={{ padding: '5px 10px', borderRadius: 5, border: `1px solid ${C.border}`, color: C.textMuted, fontFamily: 'Space Mono, monospace', fontSize: 10, textDecoration: 'none' }}
                >
                  ↗
                </a>
              </div>
              {showVisibility && publicSettings && (
                <VisibilitySettings
                  settings={publicSettings}
                  saving={visibilitySaving}
                  onChange={async (next) => {
                    setPublicSettings(next)
                    setVisibilitySaving(true)
                    try { await updateParticipantVisibility(participant.id, next) }
                    catch (e) { console.error(e) }
                    finally { setVisibilitySaving(false) }
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}
    </GlassPanel>
  )
}

// ─── Главный компонент ─────────────────────────────────────────────────────

export default function ParticipantsTab({ telegramData, threadsData, isOwner }) {
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  // Ручное добавление участника
  const [showManualAdd, setShowManualAdd] = useState(false)
  const [manualName, setManualName] = useState('')
  const [manualIsOwner, setManualIsOwner] = useState(false)
  const [manualCreating, setManualCreating] = useState(false)
  const [manualCreated, setManualCreated] = useState(null) // { name, password }
  const [addMode, setAddMode] = useState('manual') // 'manual' | 'playlist'
  const [playlistName, setPlaylistName] = useState('')
  const [playlistUrl, setPlaylistUrl] = useState('')
  const [playlistCreating, setPlaylistCreating] = useState(false)
  const [playlistError, setPlaylistError] = useState(null)
  // Матчинг Threads DM ↔ TG/DB участников
  const [showLinkFor, setShowLinkFor] = useState(null)   // displayName Threads DM у которого открыт пикер
  const [pendingLinks, setPendingLinks] = useState({})   // { threadsName → tgSuggestionDisplayName }

  // Данные из Telegram (если загружены)
  const messages = telegramData?.messages || []
  const tgParticipants = telegramData?.participants || []
  const myName = telegramData?.myName || ''
  const mergedGroups = telegramData?.mergedGroups || []

  // Данные из Threads DM
  const dmParticipants = threadsData?.dmParticipants || []
  const dmMessages = (threadsData?.dm || []).map(m => ({
    from: m.from || '',
    text: m.text,
    date: m.date,
    chatType: 'threads_dm',
    chatName: 'Threads DM',
  }))
  const allMessages = [...messages, ...dmMessages]

  const loadParticipants = useCallback(async () => {
    setLoading(true)
    try {
      const data = await listParticipants('user')
      setParticipants(Array.isArray(data) ? data : [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadParticipants() }, [loadParticipants])

  // Участники из Telegram которых ещё нет в БД
  const savedAliases = new Set(
    participants.flatMap(p => p.aliases || [p.display_name])
  )

  // Строим список из tgParticipants + mergedGroups
  const tgSuggestions = [
    // Мёрженные группы
    ...mergedGroups.map(g => ({
      displayName: g.displayName,
      aliases: g.aliases,
      count: g.aliases.reduce((sum, a) =>
        sum + (tgParticipants.find(p => p.name === a)?.count || 0), 0),
      chats: [...new Set(g.aliases.flatMap(a =>
        tgParticipants.find(p => p.name === a)?.chats || []))],
      isMerged: true,
    })),
    // Одиночные (не вмёрженные, не я, не __self__)
    ...tgParticipants
      .filter(p => p.name !== myName && p.name !== '__self__' && !mergedGroups.flatMap(g => g.aliases).includes(p.name))
      .map(p => ({
        displayName: p.name,
        aliases: [p.name],
        count: p.count,
        chats: p.chats || [],
        isMerged: false,
      })),
  ].filter(s => !savedAliases.has(s.displayName) && !s.aliases.some(a => savedAliases.has(a)))

  // Suggestions из Threads DM
  const dmSuggestions = dmParticipants
    .filter(p => p.count > 0 && p.name !== '__self__' && !savedAliases.has(p.name))
    .map(p => ({
      displayName: p.name,
      aliases: [p.name],
      count: p.count,
      chats: p.conversations || [],
      isMerged: false,
      source: 'threads_dm',
    }))

  // Привязать Threads DM участника к существующему участнику в БД — обновляем его алиасы
  const handleLinkToExisting = async (threadsName, dbParticipant) => {
    const existing = dbParticipant.aliases?.length ? dbParticipant.aliases : [dbParticipant.display_name]
    const newAliases = [...new Set([...existing, threadsName])]
    const saved = await saveParticipant({
      id: dbParticipant.id,
      owner_user_id: 'user',
      display_name: dbParticipant.display_name,
      aliases: newAliases,
      message_count: (dbParticipant.message_count || 0) + (dmParticipants.find(p => p.name === threadsName)?.count || 0),
    })
    if (saved.error) return alert(saved.error)
    setShowLinkFor(null)
    setPendingLinks(prev => { const n = { ...prev }; delete n[threadsName]; return n })
    loadParticipants()
  }

  // Привязать Threads DM участника к TG suggestion — pending (применится при добавлении TG)
  const handleLinkToTgSuggestion = (threadsName, tgDisplayName) => {
    setPendingLinks(prev => ({ ...prev, [threadsName]: tgDisplayName }))
    setShowLinkFor(null)
  }

  // При добавлении мёрженной группы — удаляем отдельные записи с теми же алиасами
  const handleAddFromTg = async (suggestion) => {
    if (suggestion.isMerged) {
      // Найти существующих участников чьи алиасы пересекаются с группой
      const toDelete = participants.filter(p =>
        (p.aliases || [p.display_name]).some(a => suggestion.aliases.includes(a))
      )
      for (const p of toDelete) await deleteParticipant(p.id)
    }
    // Подтянуть Threads DM имена, которые были связаны с этим TG suggestion
    const linkedThreadsNames = Object.entries(pendingLinks)
      .filter(([, tgName]) => tgName === suggestion.displayName || suggestion.aliases.includes(tgName))
      .map(([threadsName]) => threadsName)
    const allAliases = [...new Set([...suggestion.aliases, ...linkedThreadsNames])]
    const extraCount = linkedThreadsNames.reduce((sum, n) => sum + (dmParticipants.find(p => p.name === n)?.count || 0), 0)
    const saved = await saveParticipant({
      owner_user_id: 'user',
      display_name: suggestion.displayName,
      aliases: allAliases,
      message_count: suggestion.count + extraCount,
    })
    if (saved.error) return alert(saved.error)
    // Очищаем использованные pending links
    if (linkedThreadsNames.length) {
      setPendingLinks(prev => {
        const n = { ...prev }
        linkedThreadsNames.forEach(k => delete n[k])
        return n
      })
    }
    loadParticipants()
  }

  const handleAddAll = async () => {
    for (const s of tgSuggestions) {
      if (s.isMerged) {
        const toDelete = participants.filter(p =>
          (p.aliases || [p.display_name]).some(a => s.aliases.includes(a))
        )
        for (const p of toDelete) await deleteParticipant(p.id)
      }
      await saveParticipant({
        owner_user_id: 'user',
        display_name: s.displayName,
        aliases: s.aliases,
        message_count: s.count,
      })
    }
    loadParticipants()
  }

  const handleAddFromDm = async (suggestion) => {
    // Если есть pending link на TG suggestion — включаем его алиасы
    const linkedTgName = pendingLinks[suggestion.displayName]
    const linkedTgSugg = linkedTgName ? tgSuggestions.find(s => s.displayName === linkedTgName) : null
    const allAliases = linkedTgSugg
      ? [...new Set([...suggestion.aliases, ...linkedTgSugg.aliases])]
      : suggestion.aliases
    const totalCount = suggestion.count + (linkedTgSugg?.count || 0)
    const saved = await saveParticipant({
      owner_user_id: 'user',
      display_name: suggestion.displayName,
      aliases: allAliases,
      message_count: totalCount,
    })
    if (saved.error) return alert(saved.error)
    if (linkedTgName) setPendingLinks(prev => { const n = { ...prev }; delete n[suggestion.displayName]; return n })
    loadParticipants()
  }

  const handleManualCreate = async () => {
    if (!manualName.trim()) return
    setManualCreating(true)
    try {
      const result = await createParticipantManual({
        ownerUserId: 'user',
        displayName: manualName.trim(),
        isOwner: manualIsOwner,
      })
      setManualCreated({ name: manualName.trim(), password: result.password })
      setManualName('')
      setManualIsOwner(false)
      loadParticipants()
    } catch (e) {
      alert(`Ошибка: ${e.message}`)
    } finally {
      setManualCreating(false)
    }
  }

  const handlePlaylistCreate = async () => {
    if (!playlistName.trim() || !playlistUrl.trim()) return
    setPlaylistCreating(true); setPlaylistError(null)
    try {
      const jobId = await createParticipantFromPlaylistStart(
        'user', playlistName.trim(), playlistUrl.trim(), getModelPref('music')
      )
      const result = await pollThreadsJob(jobId, null, 3 * 60 * 1000)
      setManualCreated({ name: playlistName.trim(), password: result.participant.password })
      setPlaylistName('')
      setPlaylistUrl('')
      loadParticipants()
    } catch (e) {
      setPlaylistError(e.message)
    } finally {
      setPlaylistCreating(false)
    }
  }

  return (
    <div style={{ fontFamily: font.body, color: C.text }}>

      {/* Модальное окно — ручное добавление */}
      {showManualAdd && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(5,5,13,0.82)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
          backdropFilter: 'blur(8px)',
        }}
          onClick={e => { if (e.target === e.currentTarget) { setShowManualAdd(false); setManualCreated(null) } }}
        >
          <div style={{
            background: '#0d0e1a', border: `1px solid ${C.borderHi}`,
            borderRadius: 16, padding: '28px 32px', width: 380, maxWidth: '90vw',
          }}>
            {manualCreated ? (
              <>
                <h3 style={{ fontFamily: font.heading, fontSize: 22, fontWeight: 400, color: C.text, margin: '0 0 16px' }}>
                  Участник создан
                </h3>
                <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
                  Передай данные для входа участнику.
                </p>
                <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 6 }}>Имя</div>
                  <div style={{ fontSize: 15, color: C.text, fontFamily: font.body }}>{manualCreated.name}</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
                  <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 6 }}>Пароль</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <code style={{ fontFamily: font.mono, fontSize: 16, color: C.accent, letterSpacing: '0.1em', flex: 1 }}>
                      {manualCreated.password}
                    </code>
                    <button
                      onClick={() => navigator.clipboard?.writeText(manualCreated.password).catch(() => {})}
                      style={{ fontSize: 11, fontFamily: font.mono, color: C.textMuted,
                        background: 'none', border: `1px solid ${C.border}`, borderRadius: 4,
                        padding: '3px 8px', cursor: 'pointer' }}
                    >
                      копировать
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => { setShowManualAdd(false); setManualCreated(null) }}
                  style={{ width: '100%', padding: '10px', borderRadius: 8,
                    border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.04)',
                    color: C.text, fontFamily: font.body, fontSize: 14, cursor: 'pointer' }}
                >
                  Готово
                </button>
              </>
            ) : (
              <>
                <h3 style={{ fontFamily: font.heading, fontSize: 22, fontWeight: 400, color: C.text, margin: '0 0 6px' }}>
                  Добавить участника
                </h3>
                <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
                  {[{ val: 'manual', label: 'Обычный' }, { val: 'playlist', label: 'По плейлисту' }].map(opt => (
                    <button
                      key={opt.val}
                      onClick={() => { setAddMode(opt.val); setPlaylistError(null) }}
                      style={{ padding: '5px 12px', borderRadius: 20,
                        border: `1px solid ${addMode === opt.val ? C.accent : C.border}`,
                        background: addMode === opt.val ? 'rgba(184,189,212,0.1)' : 'transparent',
                        color: addMode === opt.val ? C.text : C.textMuted,
                        fontFamily: font.mono, fontSize: 11, cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {addMode === 'manual' ? (
                  <>
                    <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20, lineHeight: 1.5 }}>
                      Участник без данных переписки. Профиль строится через анкетирование.
                    </p>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, color: C.textMuted, fontFamily: font.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                        Имя
                      </div>
                      <input
                        autoFocus
                        value={manualName}
                        onChange={e => setManualName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleManualCreate()}
                        placeholder="Имя участника"
                        style={{ width: '100%', background: 'rgba(255,255,255,0.04)',
                          border: `1px solid ${C.borderHi}`, borderRadius: 8,
                          padding: '10px 12px', color: C.text, fontFamily: font.body, fontSize: 14,
                          outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ fontSize: 12, color: C.textMuted, fontFamily: font.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                        Статус
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[{ val: false, label: 'Участник' }, { val: true, label: 'Владелец' }].map(opt => (
                          <button
                            key={String(opt.val)}
                            onClick={() => setManualIsOwner(opt.val)}
                            style={{ flex: 1, padding: '9px 0', borderRadius: 8,
                              border: `1px solid ${manualIsOwner === opt.val ? C.accent : C.border}`,
                              background: manualIsOwner === opt.val ? 'rgba(184,189,212,0.1)' : 'transparent',
                              color: manualIsOwner === opt.val ? C.text : C.textMuted,
                              fontFamily: font.body, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s' }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        onClick={() => setShowManualAdd(false)}
                        style={{ flex: 1, padding: '10px', borderRadius: 8,
                          border: `1px solid ${C.border}`, background: 'transparent',
                          color: C.textMuted, fontFamily: font.body, fontSize: 13, cursor: 'pointer' }}
                      >
                        Отмена
                      </button>
                      <button
                        onClick={handleManualCreate}
                        disabled={!manualName.trim() || manualCreating}
                        style={{ flex: 2, padding: '10px', borderRadius: 8,
                          border: `1px solid ${manualName.trim() ? C.accent : C.border}`,
                          background: manualName.trim() ? 'rgba(184,189,212,0.1)' : 'transparent',
                          color: manualName.trim() ? C.text : C.textMuted,
                          fontFamily: font.body, fontSize: 13, cursor: manualName.trim() ? 'pointer' : 'not-allowed',
                          transition: 'all 0.15s' }}
                      >
                        {manualCreating ? 'Создаю...' : 'Создать и получить пароль'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 20, lineHeight: 1.5 }}>
                      Слепое чтение личности по публичному плейлисту (Spotify или Яндекс.Музыка) становится
                      черновиком профиля — можно дополнить позже реальными данными.
                    </p>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, color: C.textMuted, fontFamily: font.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                        Имя
                      </div>
                      <input
                        autoFocus
                        value={playlistName}
                        onChange={e => setPlaylistName(e.target.value)}
                        placeholder="Имя участника"
                        style={{ width: '100%', background: 'rgba(255,255,255,0.04)',
                          border: `1px solid ${C.borderHi}`, borderRadius: 8,
                          padding: '10px 12px', color: C.text, fontFamily: font.body, fontSize: 14,
                          outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 12, color: C.textMuted, fontFamily: font.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                        Ссылка на плейлист
                      </div>
                      <input
                        value={playlistUrl}
                        onChange={e => setPlaylistUrl(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handlePlaylistCreate()}
                        placeholder="Spotify или Яндекс.Музыка"
                        style={{ width: '100%', background: 'rgba(255,255,255,0.04)',
                          border: `1px solid ${C.borderHi}`, borderRadius: 8,
                          padding: '10px 12px', color: C.text, fontFamily: font.body, fontSize: 14,
                          outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    {playlistError && (
                      <div style={{ marginBottom: 16, fontSize: 12, color: 'rgba(220,100,100,0.9)', fontFamily: font.body }}>
                        {playlistError}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        onClick={() => setShowManualAdd(false)}
                        style={{ flex: 1, padding: '10px', borderRadius: 8,
                          border: `1px solid ${C.border}`, background: 'transparent',
                          color: C.textMuted, fontFamily: font.body, fontSize: 13, cursor: 'pointer' }}
                      >
                        Отмена
                      </button>
                      <button
                        onClick={handlePlaylistCreate}
                        disabled={!playlistName.trim() || !playlistUrl.trim() || playlistCreating}
                        style={{ flex: 2, padding: '10px', borderRadius: 8,
                          border: `1px solid ${playlistName.trim() && playlistUrl.trim() ? C.accent : C.border}`,
                          background: playlistName.trim() && playlistUrl.trim() ? 'rgba(184,189,212,0.1)' : 'transparent',
                          color: playlistName.trim() && playlistUrl.trim() ? C.text : C.textMuted,
                          fontFamily: font.body, fontSize: 13,
                          cursor: playlistName.trim() && playlistUrl.trim() ? 'pointer' : 'not-allowed',
                          transition: 'all 0.15s' }}
                      >
                        {playlistCreating ? '⏳ Читаю плейлист...' : 'Создать и получить пароль'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Заголовок */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{ fontFamily: font.heading, fontSize: 28, fontWeight: 400, color: C.text, margin: '0 0 6px' }}>
            Свои
          </h2>
          <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>
            Профили людей из твоей переписки. Анализ строится на их сообщениях.
          </p>
        </div>
        {isOwner && (
          <button
            onClick={() => { setShowManualAdd(true); setManualCreated(null) }}
            style={{ flexShrink: 0, padding: '8px 16px', borderRadius: 8,
              border: `1px solid ${C.borderHi}`, background: 'rgba(184,189,212,0.06)',
              color: C.accent, fontFamily: font.mono, fontSize: 11,
              cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.04em' }}
          >
            + Добавить участника
          </button>
        )}
      </div>

      {/* Предложения из Telegram */}
      {isOwner && tgSuggestions.length > 0 && (
        <GlassPanel style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Из загруженного Telegram ({tgSuggestions.length})
            </span>
            <button onClick={handleAddAll}
              style={{ fontSize: 11, fontFamily: font.mono, color: C.accent,
                background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, padding: '3px 10px', cursor: 'pointer' }}>
              добавить всех
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
            {tgSuggestions.map(s => (
              <div key={s.displayName} style={{ display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 10px', borderRadius: 7, border: `1px solid ${C.border}`,
                background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, color: C.text }}>{s.displayName}</span>
                  {s.isMerged && <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 6 }}>↔ {s.aliases.join(', ')}</span>}
                  <div style={{ fontSize: 10, color: C.textMuted, marginTop: 1 }}>
                    {s.count} сообщ.{s.chats.length > 0 && ` · ${s.chats.slice(0,3).join(', ')}`}
                  </div>
                </div>
                <button onClick={() => handleAddFromTg(s)}
                  style={{ fontSize: 11, fontFamily: font.mono, color: C.accent,
                    background: C.accentDim, border: `1px solid ${C.border}`, borderRadius: 5,
                    padding: '4px 10px', cursor: 'pointer' }}>
                  + добавить
                </button>
              </div>
            ))}
          </div>
        </GlassPanel>
      )}

      {/* Предложения из Threads DM */}
      {isOwner && dmSuggestions.length > 0 && (
        <GlassPanel style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Из Threads DM ({dmSuggestions.length})
            </span>
            <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted }}>↔ привяжи к TG-участнику</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
            {dmSuggestions.map(s => {
              const linkedTgName = pendingLinks[s.displayName]
              const isPickerOpen = showLinkFor === s.displayName
              return (
                <div key={s.displayName} style={{ borderRadius: 7, border: `1px solid ${linkedTgName ? C.accent + '50' : C.border}`, background: 'rgba(255,255,255,0.02)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 13, color: C.text }}>{s.displayName}</span>
                      <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 6 }}>Threads</span>
                      {linkedTgName && (
                        <span style={{ fontSize: 10, color: C.accent, marginLeft: 8 }}>
                          ↔ {linkedTgName}
                          <button onClick={() => setPendingLinks(prev => { const n = { ...prev }; delete n[s.displayName]; return n })}
                            style={{ marginLeft: 4, background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 10, padding: 0 }}>×</button>
                        </span>
                      )}
                      <div style={{ fontSize: 10, color: C.textMuted, marginTop: 1 }}>{s.count} сообщ.</div>
                    </div>
                    {/* Кнопка привязки */}
                    {!linkedTgName && (
                      <button
                        onClick={() => setShowLinkFor(isPickerOpen ? null : s.displayName)}
                        style={{ fontSize: 10, fontFamily: font.mono, color: isPickerOpen ? C.accent : C.textMuted,
                          background: 'none', border: `1px solid ${isPickerOpen ? C.accent + '60' : C.border}`,
                          borderRadius: 5, padding: '3px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        ↔ к...
                      </button>
                    )}
                    <button onClick={() => handleAddFromDm(s)}
                      style={{ fontSize: 11, fontFamily: font.mono, color: C.accent,
                        background: C.accentDim, border: `1px solid ${C.border}`, borderRadius: 5,
                        padding: '4px 10px', cursor: 'pointer' }}>
                      + добавить
                    </button>
                  </div>
                  {/* Пикер: существующие участники БД + TG suggestions */}
                  {isPickerOpen && (
                    <div style={{ borderTop: `1px solid ${C.border}`, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        это тот же человек, что:
                      </div>
                      {/* Существующие в БД */}
                      {participants.map(p => (
                        <button key={p.id} onClick={() => handleLinkToExisting(s.displayName, p)}
                          style={{ textAlign: 'left', background: C.surface2, border: `1px solid ${C.border}`,
                            borderRadius: 5, padding: '4px 10px', cursor: 'pointer',
                            fontFamily: font.body, fontSize: 12, color: C.text }}>
                          {p.display_name}
                          <span style={{ marginLeft: 6, fontFamily: font.mono, fontSize: 10, color: C.green }}>в БД</span>
                          {p.aliases?.length > 1 && <span style={{ marginLeft: 4, fontSize: 10, color: C.textMuted }}>· {p.aliases.join(', ')}</span>}
                        </button>
                      ))}
                      {/* TG suggestions */}
                      {tgSuggestions.map(tg => (
                        <button key={tg.displayName} onClick={() => handleLinkToTgSuggestion(s.displayName, tg.displayName)}
                          style={{ textAlign: 'left', background: C.surface2, border: `1px solid ${C.border}`,
                            borderRadius: 5, padding: '4px 10px', cursor: 'pointer',
                            fontFamily: font.body, fontSize: 12, color: C.text }}>
                          {tg.displayName}
                          <span style={{ marginLeft: 6, fontFamily: font.mono, fontSize: 10, color: C.accent }}>Telegram</span>
                          <span style={{ marginLeft: 4, fontSize: 10, color: C.textMuted }}>{tg.count} сообщ.</span>
                        </button>
                      ))}
                      {participants.length === 0 && tgSuggestions.length === 0 && (
                        <div style={{ fontSize: 12, color: C.textMuted }}>Нет других участников для привязки</div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </GlassPanel>
      )}

      {/* Список участников — всегда из БД */}
      {loading ? (
        <div style={{ color: C.textMuted, fontSize: 13, padding: 16 }}>Загрузка...</div>
      ) : (
        <div>
          {participants.length > 0 && (
            <div style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase',
              letterSpacing: '0.06em', marginBottom: 12 }}>
              {participants.length} участников · {participants.filter(p => p.has_profile).length} проанализировано
              {!allMessages.length && (
                <span style={{ marginLeft: 12, color: C.gold }}>
                  · загрузи Telegram или Threads для анализа
                </span>
              )}
            </div>
          )}
          {participants.map(p => (
            <ParticipantCard
              key={p.id}
              participant={p}
              messages={allMessages}
              isOwner={isOwner}
              onUpdated={loadParticipants}
              allParticipants={participants}
            />
          ))}
          {participants.length === 0 && !loading && (
            <div style={{ color: C.textMuted, fontSize: 13, padding: '24px 0', textAlign: 'center', lineHeight: 1.6 }}>
              Нет участников.{(tgSuggestions.length + dmSuggestions.length) > 0 ? ' Добавь из списка выше.' : ' Загрузи Telegram или Threads и добавь участников.'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
